import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { Spinner } from "react-bootstrap";

// --- Componente visual para la pantalla de carga (sin cambios) ---
const LoadingOverlay = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
      color: "white",
      flexDirection: "column",
      backdropFilter: "blur(5px)",
    }}
  >
    <Spinner
      animation="border"
      role="status"
      variant="light"
      style={{ width: "3rem", height: "3rem" }}
      className="mb-3"
    >
      <span className="visually-hidden">Loading...</span>
    </Spinner>
    <h4>Procesando...</h4>
  </div>
);

// --- Definición del Contexto (sin cambios) ---
interface LoadingContextType {
  isLoading: boolean;
  wrapWithLoading: <T extends any[], R>(
    fn: (...args: T) => Promise<R>
  ) => (...args: T) => Promise<R>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

// --- Proveedor del Contexto ---
export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);

  const showLoading = () => setIsLoading(true);
  const hideLoading = () => setIsLoading(false);

  // --- INICIO DE LA SOLUCIÓN ---
  // Función "wrapper" actualizada para garantizar un tiempo mínimo de visualización
  const wrapWithLoading = <T extends any[], R>(
    fn: (...args: T) => Promise<R>
  ) => {
    return async (...args: T): Promise<R> => {
      const startTime = Date.now();
      const minDisplayTime = 400; // Tiempo mínimo en milisegundos (ej. 400ms)

      showLoading();
      try {
        // Ejecuta la función original que le pasamos
        return await fn(...args);
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minDisplayTime - elapsedTime;

        if (remainingTime > 0) {
          // Si la función terminó muy rápido, esperamos el tiempo restante
          setTimeout(hideLoading, remainingTime);
        } else {
          // Si la función tardó más que el mínimo, ocultamos el loading inmediatamente
          hideLoading();
        }
      }
    };
  };
  // --- FIN DE LA SOLUCIÓN ---

  return (
    <LoadingContext.Provider value={{ isLoading, wrapWithLoading }}>
      {isLoading && <LoadingOverlay />}
      {children}
    </LoadingContext.Provider>
  );
};

// --- Hook personalizado para usar el contexto fácilmente (sin cambios) ---
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoading debe ser usado dentro de un LoadingProvider");
  }
  return context;
};
